import { useMemo, useState } from "react";
import { getTodayKey, dayLabelShort, sortDaysInGroup } from "../utils/days";

/**
 * dailyMenu shape:
 * {
 *  enabled: true,
 *  hours: "11:00–14:30",
 *  groups: [
 *    { id,label,days:[mon..sun], soup, lines:[...], extras:[...], notes:[...] }
 *  ]
 * }
 */

export default function DailyMenuTabs({ dailyMenu }) {
  const enabled = Boolean(dailyMenu?.enabled);
  const groups = Array.isArray(dailyMenu?.groups) ? dailyMenu.groups : [];

  const today = useMemo(() => getTodayKey(), []);
  const [selectedId, setSelectedId] = useState("");
  const autoActiveId =
    !enabled || groups.length === 0
      ? ""
      : groups.find((g) => Array.isArray(g.days) && g.days.includes(today))?.id || groups[0]?.id || "";
  const activeId =
    enabled && groups.length > 0 && groups.some((g) => g.id === selectedId) ? selectedId : autoActiveId;
  const active = groups.find((g) => g.id === activeId) || null;

  if (!enabled) {
    return <div className="placeholder">Denní menu je dočasně vypnuto.</div>;
  }
  if (groups.length === 0) {
    return <div className="placeholder">Denní menu zatím není nastaveno.</div>;
  }

  return (
    <div className="dmWrap">
      <div className="dmTabs">
        {groups.map((g) => {
          const isActive = g.id === activeId;
          const days = sortDaysInGroup(g.days || []).map(dayLabelShort).join("–");
          return (
            <button
              key={g.id}
              className={`dmTab ${isActive ? "isActive" : ""}`}
              onClick={() => setSelectedId(g.id)}
              type="button"
              aria-pressed={isActive}
              title={days ? `Dny: ${days}` : ""}
            >
              {g.label || "Menu"}
            </button>
          );
        })}
      </div>

      {!active ? null : (
        <div className="dmCard">
          <div className="dmTitleRow">
            <div className="dmTitle">{active.label || "Menu"}</div>
            <div className="dmBadge">
              {(sortDaysInGroup(active.days || []).map(dayLabelShort).join("–")) || "Dny neuvedeny"}
              {dailyMenu?.hours ? ` • ${dailyMenu.hours}` : ""}
            </div>
          </div>

          {active.soup ? <div className="dmSoup">{active.soup}</div> : null}

          <div className="dmLines">
            {(active.lines || []).map((ln, idx) => {
              if (!ln) return null;

              if (ln.type === "spacer") {
                return <div key={idx} className="dmSpacer" />;
              }
              if (ln.type === "heading") {
                return (
                  <div key={idx} className="dmHeading">
                    {ln.text || ""}
                  </div>
                );
              }
              if (ln.type === "item") {
                const solo = Number.isFinite(ln.priceSolo) ? ln.priceSolo : null;
                const soup = Number.isFinite(ln.priceWithSoup) ? ln.priceWithSoup : null;
                return (
                  <div key={idx} className="dmItem">
                    <div className="dmItemText">{ln.text || ""}</div>
                    <div className="dmPrices">
                      {solo !== null ? (
                        <>
                          {solo}
                          <span className="dmKc">Kč</span>
                        </>
                      ) : (
                        <span className="dmMuted">—</span>
                      )}

                      {soup !== null ? (
                        <>
                          <span className="dmSlash">/</span>
                          {soup}
                          <span className="dmKc">Kč</span>
                        </>
                      ) : null}
                    </div>
                  </div>
                );
              }
              return null;
            })}
          </div>

          {Array.isArray(active.notes) && active.notes.length > 0 ? (
            <div className="dmNotes">
              {active.notes.map((n, i) => (
                <div key={i}>• {n}</div>
              ))}
            </div>
          ) : null}

          {Array.isArray(active.extras) && active.extras.length > 0 ? (
            <div className="dmExtras">
              {active.extras.map((x, i) => (
                <div key={i} className="dmExtraRow">
                  <div>{x.text}</div>
                  <div>
                    {Number.isFinite(x.price) ? (
                      <>
                        {x.price} <span className="dmKc">Kč</span>
                      </>
                    ) : (
                      <span className="dmMuted">—</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          <div className="dmLegend">
            Ceny: bez polévky / s polévkou
          </div>
        </div>
      )}
    </div>
  );
}
